import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { register } from "../../store/slices/authSlice";
import { Typography } from "@mui/material";
import { useFormik } from "formik";
import signupValidationSchema from "../../validation/signupValidation";
import StyledMaskedInput from "../../components/shared/StyledMaskedInput";
import {
  MainContainer,
  LoginPanel,
  Content,
  HeroImage,
  FormContainer,
  Logo,
  Form,
  Footer,
  ForgotPasswordLink,
  LogoContainer,
} from "./Login.styles";
import { StyledTextFieldGroup } from "../../components/shared/StyledTextFieldGroup";
import { ActionButton } from "../../components/shared/action-button";
import { Checkbox } from "@mui/material";

const SignupPage = ({ showNotification }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    if (auth.token) {
      navigate("/dashboard");
    }
  }, [auth.token, navigate]);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      policyAccepted: false,
      termsAccepted: false,
    },
    validationSchema: signupValidationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(
          register({
            email: values.email,
            first_name: values.firstName,
            last_name: values.lastName,
            login_location: "dashboard",
            phone_number: values.phoneNumber,
            policy_accepted: values.policyAccepted,
            terms_accepted: values.termsAccepted,
            policy_accepted_date: new Date().toISOString(),
            terms_accepted_date: new Date().toISOString(),
          })
        ).unwrap();
        showNotification("Signup successful", "success");
        navigate("/login");
      } catch (error) {
        if (error.status === 400) {
          showNotification(error.data.error, "error");
        } else if (error.status === 422) {
          const validationErrors = error.data.detail
            .map((err) => err.msg)
            .join(", ");
          showNotification(validationErrors, "error");
        } else {
          showNotification("Signup failed", "error");
        }
      }
    },
  });

  return (
    <MainContainer>
      <LoginPanel>
        <Content>
          <HeroImage
            src={require("../../assets/images/login_viewer_list1.png")}
            alt="Hero"
          />
          <FormContainer>
            <LogoContainer>
              <Logo src={require("../../assets/images/MOVU_logo_white.png")} alt="Logo" />
            </LogoContainer>
            <Form onSubmit={formik.handleSubmit}>
              <StyledTextFieldGroup
                label="Email"
                subLabel="This will become your user name."
                placeholder="example@email.com"
                variant="outlined"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
              <StyledTextFieldGroup
                label="First Name"
                placeholder="Enter first name"
                variant="outlined"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
              />
              <StyledTextFieldGroup
                label="Last Name"
                placeholder="Enter last name"
                variant="outlined"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
              />
              <StyledMaskedInput
                label="Phone Number"
                variant="outlined"
                name="phoneNumber"
                placeholder="(123) 456-7890"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={
                  formik.touched.phoneNumber &&
                  Boolean(formik.errors.phoneNumber)
                }
              />
              <Typography variant="body2" style={{ color: "#ffffff" }}>
                <Checkbox
                  type="checkbox"
                  name="policyAccepted"
                  checked={formik.values.policyAccepted}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{
                    color: "white",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    paddingLeft: "0px",
                    "&.Mui-checked": {
                      color: "white",
                    },
                  }}
                />
                I accept the{" "}
                <a
                  href="https://nexus-md.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none", color: "#c4c4ff" }}
                >
                  Policy
                </a>
              </Typography>
              <Typography variant="body2" style={{ color: "#ffffff" }}>
                <Checkbox
                  type="checkbox"
                  name="termsAccepted"
                  checked={formik.values.termsAccepted}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{
                    color: "white",
                    paddingLeft: "0px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    "&.Mui-checked": {
                      color: "white",
                    },
                  }}
                />
                I accept the{" "}
                <a
                  href="https://nexus-md.com/terms"
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none", color: "#c4c4ff" }}
                >
                  {" "}
                  Terms & Conditions
                </a>{" "}
              </Typography>
              <div style={{ marginTop: "20px" }}>
                <ActionButton type="submit" fullWidth sx={{ height: "50px" }}>
                  Sign Up
                </ActionButton>
              </div>
            </Form>
            <Footer style={{ marginTop: "20px" }}>
              <Typography variant="body2" style={{ color: "#ffffff" }}>
                Already have an account?{" "}
              </Typography>
              <Typography variant="body2" style={{ color: "#A5E0FF" }}>
                <ForgotPasswordLink href="/login">Log In</ForgotPasswordLink>
              </Typography>
              <Typography
                variant="body2"
                style={{ color: "#9DA2A7", marginTop: "10px" }}
              >
                Powered by Nexus-MD
              </Typography>
            </Footer>
          </FormContainer>
        </Content>
      </LoginPanel>
    </MainContainer>
  );
};

export default SignupPage;
